import { EnumDialogName } from '@calm/evexia-analytics';
import { AudioPlayerCombined } from '@rhg/components/media/audio-player-combined';
import { AudioPlayerOpenState } from '@rhg/components/media/audio-player-combined/types';
import { PlayerAudio } from '@rhg/components/media/types';
import { ControlButtonType } from '@rhg/components/media/video-player/types';
import { useAudio } from '@rhg/hooks/use-audio';
import { MediaTrackerFn } from '@rhg/hooks/use-media-tracker/types';
import { useTheme } from '@rhg/libs/material';
import { MediaEvent } from '@rhg/utils/media';
import {
    createContext,
    ReactElement,
    ReactNode,
    useCallback,
    useEffect,
    useState,
} from 'react';
import { AudioPlayerState, OpenAudioPlayerProps } from './types';

export const AudioPlayerContext = createContext<AudioPlayerState>(null!);

interface Props {
    children: ReactNode;
}
export const AudioPlayerProvider = ({ children }: Props) => {
    const theme = useTheme();
    const { unloadAudio } = useAudio();
    const [isOpen, setIsOpen] = useState(false);
    const [initialOpenState, setInitialOpenState] =
        useState<AudioPlayerOpenState>(false);
    const [openState, setOpenState] = useState<AudioPlayerOpenState>(false);
    const [playingAudio, setPlayingAudio] = useState<PlayerAudio | null>(null);
    const [isAutoplay, setIsAutoplay] = useState(false);
    const [title, setTitle] = useState('');
    const [subtitle, setSubtitle] = useState('');
    const [trackFn, setTrackFn] = useState<MediaTrackerFn | undefined>(
        undefined
    );
    const [onEnd, setOnEnd] = useState<(() => void) | undefined>(undefined);
    const [onClose, setOnClose] = useState<(() => void) | undefined>(undefined);
    const [trackDialog, setTrackDialog] = useState<EnumDialogName>();
    const [drawerAction, setDrawerAction] = useState<ReactElement>();
    const [dialogAction, setDialogAction] = useState<ReactElement>();
    const [description, setDescription] = useState('');
    const [imageSrc, setImageSrc] = useState('');
    const [narrators, setNarrators] = useState<string[]>([]);
    const [authors, setAuthors] = useState<string[]>([]);
    const [onControlClick, setOnControlClick] =
        useState<(buttonType: ControlButtonType) => void>();

    // Set audio and then open last
    const openAudioPlayer = useCallback(
        async ({
            audio,
            title,
            subtitle,
            description,
            imageSrc,
            initialOpenState = false,
            isAutoplay = true,
            drawerAction,
            dialogAction,
            narrators = [],
            authors = [],
            trackDialog,
            trackFn,
            onEnd,
            onClose,
            onControlClick,
        }: OpenAudioPlayerProps) => {
            setIsOpen(true);
            setInitialOpenState(initialOpenState);
            setTitle(title ?? '');
            setSubtitle(subtitle ?? '');
            setDescription(description ?? '');
            setImageSrc(imageSrc ?? '');
            setIsAutoplay(!!isAutoplay);
            setDrawerAction(drawerAction);
            setDialogAction(dialogAction);
            setNarrators(narrators);
            setAuthors(authors);
            setPlayingAudio(audio);
            setTrackDialog(trackDialog);
            setTrackFn(() => trackFn);
            setOnEnd(() => onEnd);
            setOnClose(() => onClose);
            setOnControlClick(onControlClick);
        },
        []
    );

    // Set audio and open first
    // We want this to be on a timeout so that the dialog can transition out before being unmounted
    const closeAudioPlayer = useCallback(() => {
        setOpenState(false);
        setInitialOpenState(false);
        unloadAudio?.();
        onClose?.();
        setTimeout(() => {
            setIsOpen(false);
            setTitle('');
            setSubtitle('');
            setDescription('');
            setImageSrc('');
            setIsAutoplay(false);
            setDrawerAction(undefined);
            setDialogAction(undefined);
            setNarrators([]);
            setAuthors([]);
            setPlayingAudio(null);
            setTrackDialog(undefined);
            setTrackFn(() => undefined);
            setOnEnd(() => undefined);
            setOnClose(() => undefined);
            setOnControlClick(() => undefined);
        }, theme.transitions.duration.leavingScreen);
    }, [onClose, theme.transitions.duration.leavingScreen, unloadAudio]);

    const onProgress = useCallback<MediaTrackerFn>(
        async (event) => {
            if (event.eventType === MediaEvent.Ended) {
                onEnd?.();
            }
            trackFn?.(event);
        },
        [onEnd, trackFn]
    );

    // We want the dialog to render first as close, then open up afterwards so the transition is triggered
    useEffect(() => {
        if (isOpen && initialOpenState && playingAudio) {
            setOpenState(initialOpenState);
        } else {
            setOpenState(false);
        }
    }, [
        initialOpenState,
        isOpen,
        playingAudio,
        theme.transitions.duration.enteringScreen,
    ]);

    return (
        <>
            <AudioPlayerContext.Provider
                value={{
                    isOpen,
                    openState,
                    setOpenState,
                    playingAudio,
                    openAudioPlayer,
                    closeAudioPlayer,
                }}
            >
                {children}
            </AudioPlayerContext.Provider>
            {isOpen && playingAudio && (
                <AudioPlayerCombined
                    openState={openState}
                    setOpenState={setOpenState}
                    title={title}
                    subtitle={subtitle}
                    description={description}
                    imageSrc={imageSrc}
                    autoplay={isAutoplay}
                    drawerAction={drawerAction}
                    dialogAction={dialogAction}
                    narrators={narrators}
                    authors={authors}
                    playerAudio={playingAudio}
                    onProgress={onProgress}
                    trackDialog={trackDialog}
                    onEnd={onEnd}
                    onClose={closeAudioPlayer}
                    onControlClick={onControlClick}
                />
            )}
        </>
    );
};
